import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import { compose } from "redux"
import { Link } from "react-router-dom"
import { Select, Spin, Empty } from "antd"
import { get } from "lodash"

import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"

import reducer from "./reducer"
import saga from "./saga"
import {
  makeSelectIsLoadingWordcloud,
  makeSelectTotalAnswers,
  makeSelectWords,
  makeSelectIsLoadingQuestions,
  makeSelectQuestions,
} from "./selectors"
import {
  getSentimentWordcloudWatcher,
  getQualitativeQuestionsWatcher,
} from "./actions"

import iconPencil from "../../../images/icon-pencil.svg"
import emptyThresholdImage from "../../../images/empty/sentiment-analysis.svg"
import { validate } from "../../../utils/functions"
import { loadTexts, sanitize } from "../../../utils/i18n"
import QualitativeSentimentBubbles, {
  wordsToTopics,
} from "../../../components/QualitativeSentimentBubbles"
import "./index.less"

const { Option } = Select

const ALL_QUESTIONS = "all"

// RN-16/RN-19: si la medición no supera el umbral mínimo de respuestas, el
// widget completo (link + selector + bubble chart) queda bloqueado por el
// empty por umbral, con precedencia sobre cualquier otro estado vacío
// (sin preguntas, sin burbujas — ver QualitativeSentimentBubbles).
const QUALITATIVE_RESPONSE_THRESHOLD = 10

export function WidgetQualitativeBubbles(props) {
  const {
    idMeasure,
    measureTotal,
    measureGeneral,
    idDivisionSelected,
    idDivisionParent,
    selectedSegment,
    isLoadingWordcloud,
    isLoadingQuestions,
    totalAnswers,
    words,
    questions,
    getSentimentWordcloud,
    getQualitativeQuestions,
  } = props

  const [selectedQuestion, setSelectedQuestion] = useState(ALL_QUESTIONS)

  useEffect(() => {
    if (!validate.exist(idMeasure) || !validate.exist(measureGeneral)) return

    getQualitativeQuestions({
      idMeasure,
      id_survey: measureTotal.id_survey,
      measureTotal,
      measureGeneral,
      idDivisionSelected,
      idDivisionParent,
      selectedSegment,
      id_segment: selectedSegment,
      params: {
        id_measure: idMeasure,
        id_division: idDivisionSelected || idDivisionParent,
        id_segment: selectedSegment,
      },
    })
    // measureGeneral/measureTotal se re-cargan async (independiente) al cambiar
    // de segmento vía ReportAdmin.getStatus — sin esta dependencia el fetch de
    // acá dispara con datos del segmento anterior (id_survey stale) y, si falla,
    // queda pegado en [] porque nada vuelve a disparar el efecto (VLENG-11404).
    // Se depende de los valores (id_measure, id_survey) y no de los objetos: cada
    // getStatus trae objetos nuevos con los mismos datos y duplicaba los requests (VLENG-12055).
  }, [idMeasure, idDivisionSelected, selectedSegment, measureGeneral?.id_measure, measureTotal?.id_survey])

  useEffect(() => {
    if (!validate.exist(idMeasure) || !validate.exist(measureGeneral)) return

    getSentimentWordcloud({
      idMeasure,
      measureGeneral,
      idQuestion:
        selectedQuestion === ALL_QUESTIONS ? null : selectedQuestion,
      idDivisionSelected,
      idDivisionParent,
      selectedSegment,
    })
  }, [idMeasure, idDivisionSelected, selectedSegment, selectedQuestion, measureGeneral?.id_measure])

  const topics = useMemo(() => wordsToTopics(words), [words])

  if (isLoadingQuestions || isLoadingWordcloud) {
    return (
      <div className="boxShadow WidgetQualitativeBubbles loadingSpin">
        <Spin size="large" />
      </div>
    )
  }

  const responseRate = get(measureTotal, "rate.rate", 100)
  if (responseRate < QUALITATIVE_RESPONSE_THRESHOLD) {
    const intlEmptyThreshold = loadTexts("reports_qualitative_questions_view")
    return (
      <div className="boxShadow WidgetQualitativeBubbles" style={{ marginTop: "1.2rem" }}>
        <div className="WidgetQualitativeBubbles-header">
          <h4>
            <img src={iconPencil} alt="icon-pencil" style={{ marginRight: 5 }} />
            Preguntas cualitativas
          </h4>
        </div>
        <Empty
          style={{ color: "#828282" }}
          image={emptyThresholdImage}
          description={
            <span style={{ margin: "24px auto 0", maxWidth: 480, display: "block" }}>
              {sanitize(intlEmptyThreshold.emptyThreshold)}
            </span>
          }
        />
      </div>
    )
  }

  if (!validate.exist(questions) || questions.length === 0) {
    return null
  }

  return (
    <div className="boxShadow WidgetQualitativeBubbles" style={{ marginTop: "1.2rem" }}>
      <div className="WidgetQualitativeBubbles-header">
        <h4>
          <img src={iconPencil} alt="icon-pencil" style={{ marginRight: 5 }} />
          Preguntas cualitativas
        </h4>
        <span className="WidgetQualitativeBubbles-total">
          {totalAnswers} respuestas
        </span>
      </div>

      <div className="WidgetQualitativeBubbles-toolbar">
        <Link
          className="WidgetQualitativeBubbles-link"
          to={`/reports/measure/${idMeasure}/sentiment-analysis${
            selectedSegment ? `?id_segment=${selectedSegment}` : ""
          }`}>
          Ver todas las respuestas &gt;
        </Link>

        <Select
          showSearch
          value={selectedQuestion}
          optionFilterProp="children"
          onChange={(value) => setSelectedQuestion(value)}
          style={{ width: "50%" }}>
          <Option value={ALL_QUESTIONS}>Todas las preguntas</Option>
          {questions.map((question) => (
            <Option key={question.id_question} value={question.id_question}>
              {question.name}
            </Option>
          ))}
        </Select>
      </div>

      <QualitativeSentimentBubbles topics={topics} />
    </div>
  )
}

WidgetQualitativeBubbles.propTypes = {
  idMeasure: PropTypes.number,
  measureTotal: PropTypes.object,
  measureGeneral: PropTypes.object,
  idDivisionSelected: PropTypes.number,
  idDivisionParent: PropTypes.number,
  selectedSegment: PropTypes.number,
  isLoadingWordcloud: PropTypes.bool,
  isLoadingQuestions: PropTypes.bool,
  totalAnswers: PropTypes.number,
  words: PropTypes.array,
  questions: PropTypes.array,
  getSentimentWordcloud: PropTypes.func,
  getQualitativeQuestions: PropTypes.func,
}

const mapStateToProps = createStructuredSelector({
  isLoadingWordcloud: makeSelectIsLoadingWordcloud(),
  totalAnswers: makeSelectTotalAnswers(),
  words: makeSelectWords(),
  isLoadingQuestions: makeSelectIsLoadingQuestions(),
  questions: makeSelectQuestions(),
})

function mapDispatchToProps(dispatch) {
  return {
    getSentimentWordcloud: (params) =>
      dispatch(getSentimentWordcloudWatcher(params)),
    getQualitativeQuestions: (params) =>
      dispatch(getQualitativeQuestionsWatcher(params)),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withReducer = injectReducer({ key: "widgetQualitativeBubblesV2", reducer })
const withSaga = injectSaga({ key: "widgetQualitativeBubblesV2", saga })

export default compose(
  withReducer,
  withSaga,
  withConnect
)(WidgetQualitativeBubbles)
